import {
  ApplyingRule,
  ConditionalRule,
  IFormSchemaV2,
  isInputElement,
  isSingleSelectElement,
  IFieldElementUnion,
} from "@graffer-inc/form-schema";
import { ReservedApplicantIdHelper } from "../applicant-schema/applicant-reserved-ids";
import {
  ApplicantTypeLiteralList,
  ApplicantTypeLiterals,
} from "../../../application/applicant";
import type { ApplicantSchemaKeys } from "../applicant-schema-keys";

export class CloudGovFormSchemaAccessor {
  constructor(
    private readonly schema: IFormSchemaV2,
    private readonly applicantSchemaKeys: ApplicantSchemaKeys,
  ) {}

  private get root() {
    return this.schema.root;
  }

  /**
   * 申請者セクションに該当する部分を取得する。見つからなかったらUndefinedを返す
   * かつ、当該セクションが、最初に存在することを前提とする。
   */
  get applicantSection() {
    if (
      ReservedApplicantIdHelper.isApplicantSectionId(
        this.root[0].id,
        this.applicantSchemaKeys,
      )
    ) {
      return this.root[0];
    }
    return;
  }

  /**
   * 予約されている申請者の入力要素を取得する
   */
  getApplicantElement(id: IFieldElementUnion["id"]) {
    const applicantSection = this.applicantSection;
    if (!applicantSection) {
      throw new Error(`ApplicantSection does not exist.`);
    }
    return applicantSection.children.find((e) => e.id === id);
  }

  getApplyingRuleFor(
    type: ApplicantTypeLiterals,
    elemId: IFieldElementUnion["id"],
  ): ApplyingRule {
    const condRule = this.applicantSectionConditionalRule(type);
    return condRule.then.rules.find((r) => r.id === elemId)!;
  }

  /**
   * 当該スキーマにてサポートされている申請者の種別の一覧を取得する
   */
  get supportedApplicantTypes() {
    const applicantType = this.getApplicantElement(
      this.applicantSchemaKeys.applicantType.id,
    );
    // TODO: Compare
    if (
      !!applicantType &&
      isInputElement(applicantType) &&
      isSingleSelectElement(applicantType)
    ) {
      const supportedTypes = applicantType.enum.map((e) => e.value);
      if (
        supportedTypes.find(
          (e) => ApplicantTypeLiteralList.indexOf(e as any) === -1,
        )
      ) {
        throw new Error(
          "unknown applicantType enum found: " + JSON.stringify(supportedTypes),
        );
      }
      return supportedTypes as ApplicantTypeLiterals[];
    }
    throw new Error("applicantType is invalid");
  }

  get conditionalRuleSet() {
    return this.schema.conditionalRuleSet;
  }

  /**
   * ユーザーが設定する条件付きルールを取得する
   */
  get userAreaConditionalRule() {
    const rules = this.conditionalRuleSet!.conditionalRules;
    return rules.slice(0, rules.length - this.supportedApplicantTypes.length);
  }

  /**
   * ユーザーが設定した条件付きルールの最後のIndex. 設定がない場合は、-1を返す
   */
  userAreaLastIndex() {
    return (
      this.conditionalRuleSet!.conditionalRules.length -
      this.supportedApplicantTypes.length -
      1
    );
  }

  userInsertIndex() {
    const index = this.userAreaLastIndex();
    return index + 1;
  }

  /**
   * 条件付きルールの[lastIndex - supportedApplicantTypes.length, lastIndex]の要素は、CloudGovの申請者セクションのための領域
   */
  applicantSectionConditionalRule(tp: ApplicantTypeLiterals): ConditionalRule {
    const index = this.supportedApplicantTypes.indexOf(tp);
    return this.conditionalRuleSet!.conditionalRules[
      this.userAreaLastIndex() + 1 + index
    ];
  }

  applicantSectionConditionalRules() {
    const rules = this.conditionalRuleSet!.conditionalRules;
    return rules.slice(this.userAreaLastIndex() + 1);
  }
}
